import omit from "lodash/omit";
import pick from "lodash/pick";
import values from "lodash/values";
import { parseApiFilterValuesMeta } from "PFApp/components/filters_v2";
import api from "PFCore/api";
import { Filters, Id, Value } from "PFTypes";
import { ApiFilterGroupV2 } from "PFTypes/filters_v2";
import { PageTarget, SavedFilter, SavedFiltersV2 } from "PFTypes/saved_filters";

type SavedFiltersResponse = SavedFilter[];

export type SavedFiltersV2Response = SavedFiltersV2[];

const parseSavedFilters = (savedFilters: SavedFilter): SavedFiltersV2 => {
  const legacyApiFilterKeys = [...values(ApiFilterGroupV2), "children"];
  return {
    ...omit(savedFilters, legacyApiFilterKeys),
    collection: parseApiFilterValuesMeta(pick(savedFilters, legacyApiFilterKeys))
  } as SavedFiltersV2;
};

export const fetchSavedFilters = (target: PageTarget) =>
  api<SavedFiltersResponse>({
    url: "saved_filters",
    params: {
      target
    }
  }).then((savedFiltersCollection): SavedFiltersV2[] => savedFiltersCollection.map(parseSavedFilters));

export type SavedFilterCreateData = Omit<SavedFilter, "id" | "profileId">;

export const createSavedFilter = (data: SavedFilterCreateData) =>
  api<SavedFilter>({
    url: "saved_filters",
    method: "POST",
    data
  }).then(parseSavedFilters);

export type FetchLatestFiltersParams = {
  target: PageTarget;
  key?: string;
};

export type SaveLatestFiltersData = Filters<Value> & FetchLatestFiltersParams;

export const DEFAULT_LATEST_FILTERS_KEY = "__default_filters__";
const LATEST_FILTERS_KEY = "__latest__";

export const fetchLatestFilters = ({ target, key = LATEST_FILTERS_KEY }: FetchLatestFiltersParams) =>
  api<SavedFilter>({
    url: "latest_filters",
    method: "GET",
    params: {
      target,
      key
    }
  }).then(parseSavedFilters);

export const saveLatestFilters = (data: SaveLatestFiltersData) =>
  api<SavedFilter>({
    url: "latest_filters",
    method: "PUT",
    data: {
      key: LATEST_FILTERS_KEY,
      ...data
    }
  }).then(parseSavedFilters);

export const deleteSavedFilter = (id: Id) =>
  api<null>({
    url: `saved_filters/${id}`,
    method: "DELETE"
  });

export type ShareSavedFiltersData = {
  profilesIds: number[];
  name?: string;
};

export const shareSavedFilter = (
  id: Id,
  { name, profilesIds }: ShareSavedFiltersData
): Promise<SavedFilter[]> =>
  api({
    url: `saved_filters/${id}/share`,
    method: "POST",
    data: {
      name,
      profileId: profilesIds
    }
  });
